"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import axios from "axios";
import { useAuthStore } from "@/store/auth";

const KAKAO_CLIENT_ID = process.env.NEXT_PUBLIC_KAKAO_CLIENT_ID ?? "";
const KAKAO_REDIRECT_URI = process.env.NEXT_PUBLIC_KAKAO_REDIRECT_URI ?? "";

interface OAuthStatus {
  kakao: boolean;
  naver: boolean;
}

export default function LoginPage() {
  const { isLoggedIn } = useAuthStore();
  const router = useRouter();
  // β-9 후속 — OAuth 채널별 활성 상태. 네이버 키 미설정은 버튼 자동 숨김.
  const [oauthStatus, setOauthStatus] = useState<OAuthStatus | null>(null);

  useEffect(() => {
    if (isLoggedIn) router.replace("/");
  }, [isLoggedIn, router]);

  useEffect(() => {
    axios.get<OAuthStatus>("/api/auth/oauth-status")
      .then((r) => setOauthStatus(r.data))
      .catch(() => setOauthStatus({ kakao: true, naver: false }));
  }, []);

  const handleKakaoLogin = () => {
    const url =
      `https://kauth.kakao.com/oauth/authorize` +
      `?client_id=${KAKAO_CLIENT_ID}` +
      `&redirect_uri=${encodeURIComponent(KAKAO_REDIRECT_URI)}` +
      `&response_type=code`;
    window.location.href = url;
  };
  const handleNaverLogin = () => { window.location.href = "/api/auth/naver"; };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6">
      {/* 로고 */}
      <div className="mb-12 text-center">
        <div className="text-5xl mb-3">🔥</div>
        <h1 className="text-3xl font-black text-text">
          Trend<span className="text-primary">Rain</span>
        </h1>
        <p className="text-text-sub text-sm mt-2">카페 트렌드 레이더</p>
      </div>

      {/* 설명 */}
      <div className="w-full max-w-xs space-y-3 mb-10">
        {[
          ["🌱", "태동기 키워드로 선제 준비"],
          ["🔥", "성장기 아이템 빠른 도입"],
          ["📍", "지역별 포화도 분석"],
        ].map(([emoji, text]) => (
          <div key={text} className="flex items-center gap-3 card py-3">
            <span className="text-xl">{emoji}</span>
            <span className="text-sm font-medium text-text">{text}</span>
          </div>
        ))}
      </div>

      {/* OAuth 로그인 버튼 (활성 채널만 노출) */}
      <div className="w-full max-w-xs space-y-2.5">
        {/* 카카오 — 항상 활성 */}
        {(oauthStatus?.kakao ?? true) && (
          <button
            onClick={handleKakaoLogin}
            className="w-full flex items-center justify-center gap-2 bg-[#FEE500] hover:bg-[#F5DC00] text-[#191919] font-bold rounded-2xl py-3.5 text-base transition-colors"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="#191919">
              <path d="M12 3C6.48 3 2 6.48 2 11c0 2.9 1.68 5.44 4.23 7.03L5.5 21l3.37-1.76C9.54 19.42 10.74 19.6 12 19.6c5.52 0 10-3.48 10-8s-4.48-8-10-8z" />
            </svg>
            카카오로 시작하기
          </button>
        )}

        {/* 네이버 — 활성 시 */}
        {oauthStatus?.naver && (
          <button
            onClick={handleNaverLogin}
            className="w-full flex items-center justify-center gap-2 bg-[#03C75A] hover:bg-[#02B651] text-white font-bold rounded-2xl py-3.5 text-base transition-colors"
          >
            <span className="text-lg font-black">N</span>
            네이버로 시작하기
          </button>
        )}

        <p className="text-center text-xs text-text-muted mt-4">
          로그인 시{" "}
          <Link href="/terms" className="text-primary hover:underline">
            이용약관
          </Link>
          {" · "}
          <Link href="/privacy" className="text-primary hover:underline">
            개인정보처리방침
          </Link>
          에 동의하게 됩니다
        </p>
      </div>
    </div>
  );
}
